import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Button, Snackbar, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import agentRequestService from '../services/AgentRequestService';
import HelpButton from './HelpButton';
import AgentConversation from './AgentConversation';

function AgentRequests() {
    const { t } = useTranslation();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedConversationId, setSelectedConversationId] = useState(null);
    const [isConversationOpen, setIsConversationOpen] = useState(false);

    const fetchRequests = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const data = await agentRequestService.getAgentRequests();
            // Expecting an array of objects with conversationId, chatbotId, createdAt, updatedAt, agentRequestedAt
            setRequests(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error('Error fetching agent requests', e);
            setError(t('agentRequests.fetchError', 'Failed to load agent requests'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    const handleCloseError = () => {
        setError('');
    };

    const formatTimestamp = (ts) => {
        if (!ts) return '';
        return dayjs.unix(ts).format('YYYY/MM/DD - HH:mm');
    };

    const handleRowClick = (conversationId) => {
        setSelectedConversationId(conversationId);
        setIsConversationOpen(true);
    };

    const handleCloseConversation = () => {
        setIsConversationOpen(false);
        setSelectedConversationId(null);
    };

    return (
        <Box sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography variant="h5" fontWeight="bold">
                        {t('sideBar.agentRequest', 'Agent Requests')}
                    </Typography>
                    <HelpButton />
                </Box>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={fetchRequests}
                    disabled={loading}
                    sx={{ textTransform: 'none', borderRadius: 3 }}
                >
                    {loading
                        ? t('agentRequests.refreshing', 'Refreshing...')
                        : t('agentRequests.refresh', 'Refresh')}
                </Button>
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ mt: 2, borderRadius: 2 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>
                                {t('agentRequests.conversationId', 'Conversation ID')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>
                                {t('agentRequests.chatbotId', 'Chatbot ID')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>
                                {t('agentRequests.createdAt', 'Created At')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>
                                {t('agentRequests.agentRequestedAt', 'Agent Requested At')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>
                                {t('agentRequests.agent', 'Agent')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {requests.map((req) => (
                            <TableRow
                                key={req.conversationId}
                                hover
                                sx={{ cursor: 'pointer' }}
                                onClick={() => handleRowClick(req.conversationId)}
                            >
                                <TableCell>{req.conversationId}</TableCell>
                                <TableCell>{req.chatbotId}</TableCell>
                                <TableCell>{formatTimestamp(req.createdAt)}</TableCell>
                                <TableCell>{formatTimestamp(req.agentRequestedAt)}</TableCell>
                                <TableCell>{req.agent}</TableCell>
                            </TableRow>
                        ))}
                        {requests.length === 0 && !loading && (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    {t('agentRequests.empty', 'No agent requests found.')}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Snackbar
                open={Boolean(error)}
                autoHideDuration={6000}
                onClose={handleCloseError}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseError} severity="error">
                    {error}
                </Alert>
            </Snackbar>
            <AgentConversation
                open={isConversationOpen}
                onClose={handleCloseConversation}
                conversationId={selectedConversationId}
            />
        </Box>
    );
}

export default AgentRequests;
